/**
 * LiveTicker — horizontal scrolling marquee of live prices.
 * Uses useTicker hook (SSE → REST fallback, SSE retried with backoff).
 * Place at top of AppShell or Dashboard.
 */
import { useEffect, useState } from "react";
import { useTicker } from "@/hooks/useTicker";
import { Wifi, WifiOff } from "lucide-react";
import { priceLabel, tickerBadge } from "@/lib/priceLabel";
import { tickChange } from "@/lib/tickChange";

const DEFAULT_SYMBOLS = [
  "NIFTY", "BANKNIFTY", "RELIANCE", "TCS", "HDFCBANK",
  "INFY", "ICICIBANK", "SBIN", "BAJFINANCE", "TATAMOTORS",
];

function TickItem({ symbol, tick, now }) {
  if (!tick) return null;
  const label = priceLabel(tick, now);
  const chg = tickChange(tick);   // RU-TICKCHG TC-11: null change → "—"
  const up  = chg.known && chg.up;
  const clr = !chg.known ? "txt-muted" : up ? "txt-profit" : "txt-loss";
  const arrow = up ? "▲" : "▼";
  return (
    <span className="flex items-center gap-1.5 px-4 border-r border-[var(--border)] shrink-0">
      <span className="font-section tracking-wider text-xs txt-secondary">{symbol}</span>
      <span className="font-mono-data text-xs font-medium">
        ₹{Number(tick.ltp || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
      <span className={`text-xs font-mono-data ${clr}`}>
        {chg.known ? `${arrow}${Math.abs(chg.pct).toFixed(2)}%` : "—"}
      </span>
      {String(tick.source || "").startsWith("poll:daily") && (
        <span className="text-[9px] text-[var(--text-muted)] font-section tracking-wider">·D</span>
      )}
      {label && label !== "LIVE" && (
        <span className="text-[9px] text-[var(--text-muted)] font-section tracking-wider"
              data-testid={`live-ticker-label-${symbol}`}>
          {label === "SIMULATED" ? "SIM" : "DELAYED"}
        </span>
      )}
    </span>
  );
}

export default function LiveTicker({ symbols = DEFAULT_SYMBOLS }) {
  const { ticks, connected, mode } = useTicker(symbols);
  const hasData = Object.keys(ticks).length > 0;
  // RU-M4-H2 ML-7: re-evaluate freshness even when no tick arrives (feed outage)
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);
  const badge = tickerBadge(connected, ticks, symbols, now);

  return (
    <div
      data-testid="live-ticker"
      className="flex items-center border-b border-[var(--border)] bg-[var(--bg-surface)] h-8 overflow-hidden"
    >
      {/* Status indicator — RU-SIGUI2 (SU2-8): LIVE / DELAYED from the shown ticks while connected (stream or
          REST polling); OFFLINE only when neither works. Never "DELAYED" for a lost connection. */}
      <div className="flex items-center gap-1.5 px-3 border-r border-[var(--border)] shrink-0 h-full"
           title={mode === "polling" ? "Live stream reconnecting — prices refreshed every 15 s"
                  : mode === "offline" ? "No price feed connection" : undefined}
           data-mode={mode}>
        {badge === "LIVE" ? (
          <>
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--profit)] animate-pulse" />
            <span className="text-[9px] font-section tracking-widest text-[var(--profit)]" data-testid="live-ticker-badge">LIVE</span>
          </>
        ) : badge === "DELAYED" ? (
          <>
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--text-muted)]" />
            <span className="text-[9px] font-section tracking-widest text-[var(--text-muted)]" data-testid="live-ticker-badge">DELAYED</span>
          </>
        ) : (
          <>
            <span className="w-1.5 h-1.5 rounded-full border border-[var(--text-muted)]" />
            <span className="text-[9px] font-section tracking-widest text-[var(--text-muted)]" data-testid="live-ticker-badge">OFFLINE</span>
          </>
        )}
      </div>

      {/* Scrolling ticks */}
      <div
        className="flex items-center h-full overflow-x-auto scrollbar-none flex-1"
        style={{ scrollbarWidth: "none" }}
      >
        {hasData
          ? symbols.map((s) => <TickItem key={s} symbol={s} tick={ticks[s]} now={now} />)
          : connected
            ? <span className="px-4 text-xs txt-muted">Fetching prices…</span>
            : <span className="px-4 text-xs txt-muted">Market closed · prices unavailable</span>
        }
      </div>

      {/* Timestamp of last update */}
      {hasData && (
        <div className="px-3 shrink-0 border-l border-[var(--border)]">
          <span className="text-[10px] font-mono-data txt-muted">
            {new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
      )}
    </div>
  );
}
